import { useQuery } from '@tanstack/react-query'
import { ReferrerData, fetchReferrerData } from './api/unauth_api/referrer'
import MainErrorBoundary from './components/errors/MainErrorBoundary'
import LoadingWelcome from './components/unauthenticated/LoadingWelcome'
import AppInitialisationContainer from './containers/AppInititialisationContainer'
import Main from './containers/Main'
import useQueryParams from './hooks/useQueryParams'
import UnauthenticatedPage from './pages/UnauthenticatedPage'
import { useRecoilState } from 'recoil'
import { referrerState } from './store/DomainStore'
import { useEffect } from 'react'
import { isGuidedOnboarding } from './util/referrers'
import { Navigate, Route, Routes } from 'react-router-dom'
import { GuidedOnboardingLayout } from './containers/GuidedOnboardingLayout'
import IntroStepPage from './pages/guidedOnboarding/IntroStepPage'
import AuthenticateStepPage from './pages/guidedOnboarding/AuthenticateStepPage'
import { useAuth } from './auth/AuthContext'

/**
 * We use the Amplify UI Authenticator component to authenticate against Cognito.
 * See here on how to do further configuration
 * https://ui.docs.amplify.aws/components/authenticator#headers--footers
 *
 * Auth state and functions are read through the `useAuth` facade rather than
 * Amplify's useAuthenticator directly, so demo mode can swap in its own
 * implementation without touching call sites.
 */

function App() {
    const { route } = useAuth()
    const query = useQueryParams()

    const referrerUuid = query.get('referrer')

    const { data: referrerData, isLoading: isReferrerLoading } = useQuery<ReferrerData, Error>({
        queryKey: ['referrerData', referrerUuid],
        queryFn: () => {
            if (!referrerUuid) {
                return Promise.reject(new Error('Referrer UUID is undefined'))
            }
            return fetchReferrerData(referrerUuid)
        },
        retry: false,
        refetchOnWindowFocus: false,
        enabled: !!referrerUuid // Enable the query only if referrerUuid is defined
    })

    const [referrer, setReferrer] = useRecoilState(referrerState)

    useEffect(() => {
        if (!!referrerData?.referrer) {
            setReferrer(referrerData.referrer)
        }
        // eslint-disable-next-line
    }, [referrerData])

    const hasReachedUserLimit = referrerData?.hasReachedUserLimit

    function generateAuthenticated() {
        return (
            <AppInitialisationContainer>
                <div className="relative full-screen">
                    <div className="full-screen">
                        <Main />
                    </div>
                </div>
            </AppInitialisationContainer>
        )
    }

    function generateUnauthenticated() {
        if (!isReferrerLoading) {
            return isGuidedOnboarding(referrer) ? (
                <Routes>
                    <Route path="/" element={<GuidedOnboardingLayout />}>
                        <Route path="/" element={<IntroStepPage />}></Route>
                        <Route path="/authenticate" element={<AuthenticateStepPage />}></Route>
                        <Route path="/*" element={<Navigate to="/" replace />}></Route>
                    </Route>
                </Routes>
            ) : (
                <UnauthenticatedPage hasReachedUserLimit={hasReachedUserLimit} />
            )
        }
    }

    const generateLoading = () => {
        return <LoadingWelcome />
    }

    const generateContent = () => {
        if (isReferrerLoading) {
            return generateLoading()
        } else {
            if (route === 'authenticated') {
                return generateAuthenticated()
            } else {
                return generateUnauthenticated()
            }
        }
    }

    return <MainErrorBoundary>{generateContent()}</MainErrorBoundary>
}

export default App
